import { useMemo } from 'react';

import { Button } from 'react-bootstrap';

import ExpandableSection from '@components/ExpandableSection/ExpandableSection';
import { SUPPORT_EMAIL } from '@constants/constants';
import { PatientRerequisitionEligibility } from '@customTypes/patient';

import NoDetailContent from '../NoDetailContent/NoDetailContent';
import NewReportCard from './NewReportCard';

import './PatientDetailReports.scss';

interface PatientDetailReportsProps {
  allowGeneralRerequisition?: boolean;
  rerequisitionEligibility: PatientRerequisitionEligibility | null;
}

export const PatientDetailReports: React.FC<PatientDetailReportsProps> = props => {
  const { rerequisitionEligibility, allowGeneralRerequisition = true } = props;

  const noReports = useMemo(() => {
    if (!rerequisitionEligibility) {
      return false;
    }

    if (allowGeneralRerequisition && rerequisitionEligibility.new_order_products.length) {
      return false;
    }

    return !rerequisitionEligibility.campaign || !rerequisitionEligibility.campaign_products?.length;
  }, [rerequisitionEligibility, allowGeneralRerequisition]);

  return (
    <ExpandableSection
      id='newReports'
      expandableContentId='newReportsContent'
      headerTitle={'New Reports Available for Ordering'}
    >
      {!!rerequisitionEligibility && !noReports && (
        <div className='d-flex flex-column flex-md-row gap-3 gap-lg-4 new-reports'>
          {!!rerequisitionEligibility.campaign && !!rerequisitionEligibility.campaign_products?.length && (
            <NewReportCard
              campaign={rerequisitionEligibility.campaign}
              originalRequisitionId={rerequisitionEligibility.original_requisition_id}
              products={rerequisitionEligibility.campaign_products}
            />
          )}

          {allowGeneralRerequisition && !!rerequisitionEligibility.new_order_products.length && (
            <NewReportCard
              originalRequisitionId={rerequisitionEligibility.original_requisition_id}
              products={rerequisitionEligibility.new_order_products}
            />
          )}
        </div>
      )}

      {noReports && <NoDetailContent content='There are no new reports available at this time.' />}

      {!rerequisitionEligibility && (
        <NoDetailContent content='Ordering new reports is only available once an existing order is complete.' />
      )}

      <p className='fs-sm mb-0 text-secondary'>
        <span className='d-block'>
          The list of available reports is based off your patient and their previous orders, and no additional sample
          will be required.
        </span>

        <span className='d-block'>
          <span className='d-inline-block align-middle'>If you have questions, please contact</span>
          <Button variant='link' className='p-0 border-0 ms-1 fs-sm' href={`mailto:${SUPPORT_EMAIL}`} target='_blank'>
            {SUPPORT_EMAIL}.
          </Button>
        </span>
      </p>
    </ExpandableSection>
  );
};
